import {registerPlugin} from '@capacitor/core'

/**
 * The bridge handle for the local `NativePushPlugin` (#1695) — raw
 * `UNUserNotificationCenter`/APNs on iOS (`ios/App/CapApp-SPM/Sources/
 * CapApp-SPM/NativePushPlugin.swift`), real Firebase Cloud Messaging on
 * Android (`android/.../NativePushPlugin.java`). Same "app-local native
 * plugin, not an npm package" pattern as #1691's `nativeSecureStorage.js` —
 * see that file's own comment for why (avoids a `package.json`/`yarn.lock`
 * change this ticket is also forbidden from making).
 *
 * Surface (implemented natively, called from `push.js`):
 *  - `requestPermission()` -> `{granted: boolean}` — prompts the OS
 *    notification permission (§12.1 PUSH_PERMISSION_BLOCKED maps to a
 *    `granted: false` result here, not a thrown error — refusing is a valid
 *    user choice, not a fault).
 *  - `readPermission()` -> `{status: 'granted'|'denied'|'prompt'}` — no
 *    prompt, safe to call on every render (mirrors messaging.js's
 *    readPushPermission contract).
 *  - `getToken()` -> `{token: string|null}` — the current APNs/FCM
 *    installation token, if the OS has already handed one to the native
 *    layer (iOS: after `didRegisterForRemoteNotificationsWithDeviceToken`;
 *    Android: the FCM SDK's own cached instance id token).
 *  - `getLastTappedPush()` -> `{data: object}|{data: null}` — mirrors
 *    `@capacitor/app`'s `getLaunchUrl()`: the payload of the push that
 *    launched the app from a killed state via a tap, if any
 *    (`nativePushTapListener.js`'s cold-start case).
 *  - Events (via `addListener`, per Capacitor's plugin contract):
 *    - `'registration'` — `{token: string}`, fired whenever the OS hands the
 *      native layer a fresh/rotated token.
 *    - `'registrationError'` — `{error: string}`.
 *    - `'pushReceived'` — `{title, body, data}` — a push arrived while the
 *      app was in the FOREGROUND (the OS does not auto-display in this
 *      case — see push.js's initForegroundDisplay).
 *    - `'pushTapped'` — `{data}` — the user tapped a system-displayed
 *      notification (app was backgrounded/killed), OR tapped one this app
 *      itself displayed via `pushReceived` — `nativePushTapListener.js` is
 *      the one place that turns this into navigation.
 */
export const NativeNotifications = registerPlugin('NativePush')
